import deepEqual from 'fast-deep-equal';
import { Box3, Matrix4, Object3D, Sphere, Vector2Tuple, Vector3 } from 'three';

import { isPolygonInNavMesh, NavMeshQuery } from '@spazious/dollhouse-navmesh';
import { Outline2D } from '@spazious/dollhouse-three';
import { uuid } from '@spazious/dollhouse-utils';

import { Schema, SchemaImpl, SchemaValues, SchemaVariables } from '../schema/index.js';
import { FInstanceNode } from '../types/index.js';

import { FEnsemble } from './types.js';

import '@spazious/dollhouse-three/shims';

const mat4 = new Matrix4();
const vertex = new Vector3();

/**
 * Abstract base class for manage furniture compositions.
 *
 * Given a schema definition, this class provides the basic structure and methods for managing compositions.
 */
export abstract class FEnsembleBase<TSchema extends Schema, TVariables extends SchemaVariables>
  extends SchemaImpl<TSchema, TVariables>
  implements FEnsemble<TSchema, TVariables>
{
  uuid = '';
  /**
   * Bounding box for the {@link THREE.BufferGeometry | BufferGeometry}, which can be calculated with {@link computeBoundingBox | .computeBoundingBox()}.
   * @remarks Bounding boxes aren't computed by default. They need to be explicitly computed, otherwise they are `null`.
   * @defaultValue `null`
   */
  boundingBox: Box3 | null = null;

  /**
   * Computes bounding box of the geometry, updating {@link boundingBox | .boundingBox} attribute.
   * @remarks Bounding boxes aren't computed by default. They need to be explicitly computed, otherwise they are `null`.
   */
  computeBoundingBox(): void {
    const nodeTypes = this.getNodeTypes();
    const objects = this.getObjects();
    if (nodeTypes.length !== objects.length) {
      throw new Error(
        `Number of node types (${nodeTypes.length}) and objects (${objects.length}) must match`
      );
    }
    const nodeObjectMap = Object.fromEntries(nodeTypes.map((type, idx) => [type, objects[idx]]));

    if (!this.boundingBox) {
      this.boundingBox = new Box3().makeEmpty();
    }

    this.traverseNodes((node, parent) => {
      const object = nodeObjectMap[node.id];
      if (!object) {
        throw new Error(`Object not found for node type ${node.id}`);
      }
      const bbox = new Box3().setFromObject(object);
      this.boundingBox!.union(
        bbox
          .applyMatrix4(mat4.makeRotationY((parent?.rotation ?? 0) + node.rotation))
          .applyMatrix4(
            mat4.makeTranslation(
              (parent?.position[0] ?? 0) + node.position[0],
              0,
              (parent?.position[1] ?? 0) + node.position[1]
            )
          )
      );
    });
  }

  /**
   * set of points representing the outer edge of the ensemble when projected to XZ plane
   */
  outline2D: Outline2D | null = null;

  /**
   * Computes 2D outline of the fensemble, updating {@link outline2D | .outline2D} attribute.
   * @remarks outline2D is not computed by default. It needs to be explicitly computed, otherwise it will be `null`.
   */
  computeOutline2D() {
    const nodeTypes = this.getNodeTypes();
    const objects = this.getObjects();
    if (nodeTypes.length !== objects.length) {
      throw new Error(
        `Number of node types (${nodeTypes.length}) and objects (${objects.length}) must match`
      );
    }
    const objectOutlines: Vector2Tuple[][] = objects.map((object) => {
      if (!object.outline2D) {
        object.computeOutline2D();
      }
      return object.outline2D!.array;
    });

    const nodeOutlineMap = Object.fromEntries(
      nodeTypes.map((type, idx) => [type, objectOutlines[idx]])
    );

    const vertices: Vector2Tuple[] = [];

    this.traverseNodes((node, parent) => {
      if (!nodeOutlineMap[node.id]) {
        throw new Error(`Geometry not found for node type ${node.id}`);
      }

      const objectVertices = nodeOutlineMap[node.id];
      const vertex = new Vector3();
      for (let v of objectVertices) {
        vertex.set(v[0], 0, v[1]);
        vertex
          .applyMatrix4(mat4.makeRotationY((parent?.rotation ?? 0) + node.rotation))
          .applyMatrix4(
            mat4.makeTranslation(
              (parent?.position[0] ?? 0) + node.position[0],
              0,
              (parent?.position[1] ?? 0) + node.position[1]
            )
          );
        vertices.push([vertex.x, vertex.z]);
      }
    });

    this.outline2D = new Outline2D().setFromPoints(vertices);
  }

  /**
   * Bounding sphere for the {@link THREE.BufferGeometry | BufferGeometry}, which can be calculated with {@link computeBoundingSphere | .computeBoundingSphere()}.
   * @remarks bounding spheres aren't computed by default. They need to be explicitly computed, otherwise they are `null`.
   * @defaultValue `null`
   */
  boundingSphere: Sphere | null = null;

  /**
   * Computes bounding sphere of the geometry, updating {@link boundingSphere | .boundingSphere} attribute.
   * @remarks bounding spheres aren't computed by default. They need to be explicitly computed, otherwise they are `null`.
   */
  computeBoundingSphere() {
    if (!this.boundingBox) {
      this.computeBoundingBox();
    }

    if (!this.boundingSphere) {
      this.boundingSphere = new Sphere();
    }

    this.boundingBox!.getBoundingSphere(this.boundingSphere);
  }

  /**
   * The objects for the arrangement
   */
  objects: Object3D[] = [];

  /**
   * Method to set the objects.
   * This will mark the schema and attributes as needing an update if the objects have changed.
   */
  setObjects(objects: Object3D[]) {
    if (!deepEqual(this.objects, objects)) {
      this.objects = objects;
      this.setNodesNeedUpdate(true);
      this.updateObjectMeasurements();
      return true;
    }

    return false;
  }

  /**
   * Get the objects for the arrangement
   */
  getObjects(): Object3D[] {
    return this.objects;
  }

  protected updateObjectMeasurements() {
    throw new Error('FEnsemble.updateObjectMeasurements Method not implemented.');
  }

  /**
   * Get the node types for the given objects and attributes
   */
  getNodeTypes(): number[] {
    throw new Error('FEnsemble.getNodeTypes Method not implemented.');
  }

  protected nodesNeedUpdate = true;
  protected nodes: FInstanceNode[] = [];

  setNodesNeedUpdate(value: boolean) {
    this.nodesNeedUpdate = value;
    if (this.nodesNeedUpdate) {
      // reset values that depend on nodes list
      this.boundingBox = null;
      this.boundingSphere = null;
      this.outline2D = null;
    }
  }

  private _lastSetValues: Partial<SchemaValues<TSchema['fields']> & TVariables> | null = null;

  setValues(values: Partial<SchemaValues<TSchema['fields']> & TVariables>): this {
    // A translate/rotate-only commit re-derives the shallow graph twice
    // (staged preview, then the committed history entry) with the exact
    // same attrs both times — skip the redundant computeNodes() when
    // nothing actually changed since the last call.
    if (!deepEqual(values, this._lastSetValues)) {
      // copy, not alias — the caller mutating its own object later must not
      // silently change what we compare against next time
      this._lastSetValues = { ...values };
      this.setNodesNeedUpdate(true);
    }
    return super.setValues(values);
  }
  /**
   * Get the nodes for the given objects and attributes
   */
  getNodes(): FInstanceNode[] {
    if (this.nodesNeedUpdate) {
      this.nodes = this.computeNodes();
      this.setNodesNeedUpdate(false);
    }
    return this.nodes;
  }

  computeNodes(): FInstanceNode[] {
    throw new Error('FEnsemble.computeNodes Method not implemented.');
  }

  traverseNodes(fn: (node: FInstanceNode, parent: FInstanceNode | null) => void) {
    const nodes = this.getNodes();

    const traverse = (node: FInstanceNode, parent: FInstanceNode | null) => {
      fn(node, parent);
      if (node.children) {
        node.children.forEach((n) => traverse(n, node));
      }
    };

    nodes.forEach((n) => traverse(n, null));
  }

  getSafeIterations(count: number): number {
    if (!Number.isFinite(count) || Number.isNaN(count) || !Number.isInteger(count)) {
      console.warn('Invalid count');
      return 1;
    }
    return count;
  }

  /**
   * Get the node uuid for the given type and index
   */
  getNodeUuid(id: number, index: number = 0) {
    return uuid(`${this.uuid}-${id}-${index}`);
  }

  private _position: Vector2Tuple = [0, 0];

  get position(): Vector2Tuple {
    return this._position;
  }

  set position(value: Vector2Tuple) {
    // same reasoning as setValues: a redundant re-derive of the same final
    // position (staged preview vs. committed history) must not re-trigger
    // computeNodes().
    if (!deepEqual(this._position, value)) {
      // copy, not alias — same reasoning as setValues() above
      this._position = [...value];
      this.setNodesNeedUpdate(true);
    }
  }

  private _rotation: number = 0;

  get rotation(): number {
    return this._rotation;
  }

  set rotation(value: number) {
    if (this._rotation !== value) {
      this._rotation = value;
      this.setNodesNeedUpdate(true);
    }
  }

  navMeshQuery: NavMeshQuery | null = null;

  setNavMeshQuery(navMeshQuery: NavMeshQuery | null) {
    if (this.navMeshQuery === navMeshQuery) {
      return;
    }
    this.navMeshQuery = navMeshQuery;
    // computeNodes() filters children against navMeshQuery, so a swapped
    // query invalidates the cached layout the same way setValues() does.
    this.setNodesNeedUpdate(true);
  }

  getNavMeshQuery(): NavMeshQuery | null {
    return this.navMeshQuery;
  }

  objectFromNode(node: FInstanceNode): Object3D {
    const nodeTypes = this.getNodeTypes();
    const index = nodeTypes.indexOf(node.id);
    return this.getObjects()[index];
  }

  outlineFromNode(node: FInstanceNode): Vector2Tuple[] {
    let outline: Vector2Tuple[] = [];

    const obj = this.objectFromNode(node);
    if (!obj.outline2D) {
      obj.computeOutline2D();
    }

    outline = ((obj.outline2D?.array ?? []) as Vector2Tuple[]).map((p) => {
      vertex.set(p[0], 0, p[1]);

      // Then apply node's own transformations
      vertex
        .applyMatrix4(mat4.makeRotationY(node.rotation))
        .applyMatrix4(mat4.makeTranslation(node.position[0], 0, node.position[1]));

      // Apply parent transformations first if they exist
      vertex
        .applyMatrix4(mat4.makeRotationY(this.rotation))
        .applyMatrix4(mat4.makeTranslation(this.position[0], 0, this.position[1]));

      return [vertex.x, vertex.z];
    });

    return outline;
  }

  isNodeWithinNavmesh(node: FInstanceNode): boolean {
    const outline = this.outlineFromNode(node);
    if (!outline || !outline.length) {
      console.warn('Node missing outline 2d', node);
      return true;
    }
    const navMeshQuery = this.getNavMeshQuery();
    if (!navMeshQuery) {
      console.warn('missing navmesh query', this);
      return true;
    }

    return isPolygonInNavMesh(outline, navMeshQuery);
  }
}
